import { useCallback, useMemo } from 'react';
import {
  addWeeks,
  addMonths,
  addYears,
  addDays,
  subWeeks,
  subMonths,
  subYears,
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfWeek,
  endOfMonth,
  endOfYear,
  format,
  parseISO,
} from 'date-fns';
import { useScheduleStore, type ViewMode } from '../stores/scheduleStore';

/**
 * Hook for navigating the visible date range based on the current view mode.
 *
 * Provides:
 * - goToToday: Jump the view to include today's date
 * - goForward: Advance by one period
 * - goBackward: Go back by one period
 * - dateRangeLabel: Human-readable label for the current range
 */
export function useDateNavigation() {
  const { viewMode, dateRange, setDateRange, firstDayOfWeek } = useScheduleStore();

  /**
   * Calculate the date range for a given anchor date and view mode.
   */
  const calculateRange = useCallback(
    (anchor: Date, mode: ViewMode) => {
      switch (mode) {
        case 'week-daily': {
          const weekStart = startOfWeek(anchor, { weekStartsOn: firstDayOfWeek });
          const weekEnd = endOfWeek(anchor, { weekStartsOn: firstDayOfWeek });
          return {
            start: weekStart.toISOString(),
            end: weekEnd.toISOString(),
          };
        }
        case '2weeks-daily': {
          const weekStart = startOfWeek(anchor, { weekStartsOn: firstDayOfWeek });
          return {
            start: weekStart.toISOString(),
            end: addDays(weekStart, 13).toISOString(),
          };
        }
        case 'month-daily': {
          const monthStart = startOfMonth(anchor);
          const monthEnd = endOfMonth(anchor);
          return {
            start: monthStart.toISOString(),
            end: monthEnd.toISOString(),
          };
        }
        case 'year-monthly':
        case 'year-daily': {
          const yearStart = startOfYear(anchor);
          const yearEnd = endOfYear(anchor);
          return {
            start: yearStart.toISOString(),
            end: yearEnd.toISOString(),
          };
        }
      }
    },
    [firstDayOfWeek],
  );

  /** Navigate to include today's date. */
  const goToToday = useCallback(() => {
    const range = calculateRange(new Date(), viewMode);
    setDateRange(range);
  }, [viewMode, calculateRange, setDateRange]);

  /** Move forward by one view period. */
  const goForward = useCallback(() => {
    const currentStart = parseISO(dateRange.start);
    let nextAnchor: Date;

    switch (viewMode) {
      case 'week-daily':
        nextAnchor = addWeeks(currentStart, 1);
        break;
      case '2weeks-daily':
        nextAnchor = addWeeks(currentStart, 2);
        break;
      case 'month-daily':
        nextAnchor = addMonths(currentStart, 1);
        break;
      case 'year-monthly':
      case 'year-daily':
        nextAnchor = addYears(currentStart, 1);
        break;
    }

    const range = calculateRange(nextAnchor, viewMode);
    setDateRange(range);
  }, [viewMode, dateRange.start, calculateRange, setDateRange]);

  /** Move backward by one view period. */
  const goBackward = useCallback(() => {
    const currentStart = parseISO(dateRange.start);
    let prevAnchor: Date;

    switch (viewMode) {
      case 'week-daily':
        prevAnchor = subWeeks(currentStart, 1);
        break;
      case '2weeks-daily':
        prevAnchor = subWeeks(currentStart, 2);
        break;
      case 'month-daily':
        prevAnchor = subMonths(currentStart, 1);
        break;
      case 'year-monthly':
      case 'year-daily':
        prevAnchor = subYears(currentStart, 1);
        break;
    }

    const range = calculateRange(prevAnchor, viewMode);
    setDateRange(range);
  }, [viewMode, dateRange.start, calculateRange, setDateRange]);

  /** Human-readable label for the currently visible date range. */
  const dateRangeLabel = useMemo(() => {
    const start = parseISO(dateRange.start);
    const end = parseISO(dateRange.end);

    switch (viewMode) {
      case 'week-daily':
      case '2weeks-daily':
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
      case 'month-daily':
        return format(start, 'MMMM yyyy');
      case 'year-monthly':
      case 'year-daily':
        return format(start, 'yyyy');
    }
  }, [viewMode, dateRange]);

  return {
    goToToday,
    goForward,
    goBackward,
    dateRangeLabel,
    calculateRange,
  };
}
